import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuid4 } from 'uuid';
import { AuthService } from '../../../../services/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { User } from '../../../../interfaces';
import { MtmEditAssetTechnicalVariantsComponent } from './components/technical-variants-table/edit-asset-technical-variants.component';
import { EditAssetModalHelperService } from '../../services/edit-asset-modal-helper.service';
import { AssetPropertiesAvailabilityService } from '../../services/asset-properties-availability.service';
import {
  DEFAULT_ASSET_FOCUS,
  DEFAULT_ASSET_TYPE,
  EDIT_ASSET_BETS_OPTIONS,
  EDIT_ASSET_FORMAT_DISPLAY_MAP,
  EDIT_ASSET_FORMAT_OPTIONS,
  EDIT_ASSET_LENGTH_OPTIONS,
  LENGTH_STATUS_SHORT,
  MASTER_ASSET_TYPE,
  TECHNICAL_VARIANT_ASSET_TYPE,
} from '../../constants/edit-asset-modal.constants';
import {
  Asset,
  AssetPropertiesAvailability,
  LengthState,
  PrimaryAssetFields,
  SelectionState,
  SubAsset,
  VariantState,
} from '../../models/asset.model';

@Component({
  selector: 'mtm-edit-asset-modal',
  templateUrl: './edit-asset-modal.component.html',
  styleUrl: './edit-asset-modal.component.scss',
  standalone: false,
})
export class MtmEditAssetModalComponent implements OnInit, OnDestroy {
  @ViewChild(MtmEditAssetTechnicalVariantsComponent)
  technicalVariantsComponent?: MtmEditAssetTechnicalVariantsComponent;
  @Input() affectedAssets: Record<string, { subAsset: SubAsset[]; status?: string }>;
  @Input() asset: Asset | null | undefined;
  @Input() forVariant: boolean = false;
  @Input() funnel: string;

  assets: SubAsset[] = [];
  tempAssets: SubAsset[] = [];
  tempAffectedAssets: Record<string, { subAsset: SubAsset[]; status?: string }> = {};

  availableCategories: string[] = [];
  availableFormat: string[] = [];
  availableLength: string[] = [];
  agencys: string[] = [];
  betList: string[] = [];
  focuses: string[] = [];
  funnels: string[] = [];
  regions: string[] = [];
  types: string[] = [];

  focus: string = '';
  region: string = '';
  agency: string = '';
  product: string = '';
  type: string = '';
  status: string = '';
  detail: string = '';

  releaseDate: { year: number; month: number; day: number } | null = null;
  otherBet: string;
  selectedAssetType: string = '';
  selectedCategories: string[] = [];
  selectedBets: string[] = [];
  selectedFormats: string[] = [];
  selectedLengths: string[] = [];
  selectedFormat: string = '';

  submitted: boolean = false;
  isNew: boolean = true;
  disableFunnelSelection: boolean = false;
  showError: boolean = false;
  lengthValue: string = '01"';
  lengthStatus: string = LENGTH_STATUS_SHORT;
  lengthValueInSeconds: number = 0;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly authService: AuthService,
    private readonly helper: EditAssetModalHelperService,
    private readonly assetPropertiesAvailabilityService: AssetPropertiesAvailabilityService
  ) {}

  lengthOptions: string[] = [...EDIT_ASSET_LENGTH_OPTIONS];
  formatOptions: string[] = [...EDIT_ASSET_FORMAT_OPTIONS];
  betsOptions: string[] = [...EDIT_ASSET_BETS_OPTIONS];

  readonly formatDisplayMap: typeof EDIT_ASSET_FORMAT_DISPLAY_MAP = EDIT_ASSET_FORMAT_DISPLAY_MAP;

  public getFormatDisplay(format: string): string {
    if (!format) return '';

    const formatInfo: { type: string; ratio: string } = this.formatDisplayMap[format];
    if (!formatInfo) return format;

    return `${formatInfo.type} <strong>${formatInfo.ratio}</strong>`;
  }

  public ngOnInit(): void {
    this.loadFeatureConfigs();
    this.initializeDefaults();
    this.initializeFromAsset();
    this.initializeFunnelSelection();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public addNewTechVariant(): void {
    this.ensureTempAssets();
    const variantId: string = uuid4();
    this.tempAssets.push({
      id: variantId,
      detail: '',
      length: [],
      format: [],
      bets: [],
      assetType: TECHNICAL_VARIANT_ASSET_TYPE,
    });

    if (this.status) {
      this.addVariantInAffectedList(variantId);
    }

    setTimeout(() => {
      this.technicalVariantsComponent?.scrollToBottom();
    }, 0);
  }

  public onVariantAssetUpdated(updated: { asset: Asset; index: number }): void {
    if (!updated?.asset) {
      return;
    }

    const { asset, index } = updated;
    if (asset?.id) {
      const existingIndex: number = this.tempAssets.findIndex((item: SubAsset) => item?.id === asset.id);
      if (existingIndex !== -1) {
        this.tempAssets[existingIndex] = { ...this.tempAssets[existingIndex], ...asset };
        return;
      }
    }

    if (typeof index === 'number' && index > -1 && index < this.tempAssets.length) {
      this.tempAssets[index] = { ...this.tempAssets[index], ...asset };
    }
  }

  private addVariantInAffectedList(variantId: string): void {
    if (!this.canMutateAffectedAssets()) return;

    const assetId: string = this.asset?.id;
    if (!assetId) return;

    this.ensureAffectedAssetEntry(assetId);

    this.tempAffectedAssets[assetId].subAsset = [
      ...(this.tempAffectedAssets[assetId].subAsset || []),
      { id: variantId, status: this.status },
    ];
  }

  private removeVariantInAffectedList(variantId: string | number): void {
    if (!this.canMutateAffectedAssets()) return;

    const assetId: string = this.asset?.id;
    if (!assetId) return;

    const subAssets: SubAsset[] = this.tempAffectedAssets[assetId]?.subAsset;
    if (!subAssets?.length) return;

    this.tempAffectedAssets[assetId].subAsset = subAssets.filter((sub: SubAsset) => sub.id !== variantId);
  }

  public onDelete(idOrIndex: number | string): void {
    if (typeof idOrIndex === 'number') {
      const assetId: string = this.tempAssets[idOrIndex]?.id;
      this.tempAssets.splice(idOrIndex, 1);

      if (assetId) {
        this.removeVariantInAffectedList(assetId);
      }
    } else {
      this.tempAssets = this.tempAssets.filter((a: SubAsset) => a.id !== idOrIndex);
      this.removeVariantInAffectedList(idOrIndex);
    }
  }

  public increaseLength(): void {
    const nextSeconds: number = this.lengthValueInSeconds + 1;
    if (!this.helper.isLengthInRange(nextSeconds)) {
      return;
    }

    this.applyLengthState(this.helper.buildLengthStateFromSeconds(nextSeconds));
    this.showError = false;
  }

  public decreaseLength(): void {
    const nextSeconds: number = this.lengthValueInSeconds - 1;
    if (!this.helper.isLengthInRange(nextSeconds)) {
      return;
    }

    this.applyLengthState(this.helper.buildLengthStateFromSeconds(nextSeconds));
    this.showError = false;
  }

  public onInputChange(event: Event): void {
    const input: string = (event.target as HTMLInputElement).value;
    const totalSeconds: number = this.helper.parseLengthInput(input);

    if (totalSeconds === null || !this.helper.isLengthInRange(totalSeconds)) {
      this.showError = true;
      return;
    }

    this.applyLengthState(this.helper.buildLengthStateFromSeconds(totalSeconds));
    this.showError = false;
  }

  private mapValues(): void {
    const { asset } = this;
    if (!asset) {
      return;
    }

    const fields: PrimaryAssetFields = this.helper.extractPrimaryFields(asset);
    this.region = fields.region;
    this.agency = fields.agency;
    this.product = fields.product;
    this.type = fields.type;
    this.focus = fields.focus;
    this.detail = fields.detail;

    const selectionState: SelectionState = this.helper.buildSelectionState(
      asset,
      this.betList,
      this.availableFormat,
      this.selectedFormat
    );

    this.selectedBets = selectionState.selectedBets;
    this.otherBet = selectionState.otherBet;
    this.selectedFormats = selectionState.selectedFormats;
    this.selectedLengths = selectionState.selectedLengths;
    this.selectedCategories = selectionState.selectedCategories;
    this.releaseDate = selectionState.releaseDate;
    this.applyLengthState(selectionState.lengthState);
    this.selectedFormat = selectionState.selectedFormat;

    if (!this.tempAffectedAssets) {
      this.tempAffectedAssets = {};
    }

    if (!this.affectedAssets) {
      this.affectedAssets = {};
    }

    const variantState: VariantState = this.helper.buildVariantState(asset, this.status, this.affectedAssets);

    if (variantState.statusFromAffected) {
      this.status = variantState.statusFromAffected;
    }

    this.tempAssets = variantState.tempAssets;

    if (asset.id && variantState.affectedEntry) {
      this.tempAffectedAssets[asset.id] = variantState.affectedEntry;
    }
  }

  public onFunnelSelect(val: string): void {
    this.funnel = val;
  }

  public onFocusSelect(val: string): void {
    this.focus = val;
  }

  public onRegionSelect(val: string): void {
    this.region = val;
  }

  public onAgencySelect(val: string): void {
    this.agency = val;
  }

  public onTypeSelect(val: string): void {
    this.type = val;
  }

  public onTitleSelect(val: string): void {
    this.detail = val;
  }

  public onDetaileSelect(val: string): void {
    this.detail = val;
  }

  public selectAsset(asset: string): void {
    this.selectedAssetType = asset;
  }

  public selectCategory(category: string): void {
    this.selectedCategories = this.toggleSelection(this.selectedCategories, category, true);
  }

  public selectBet(bet: string): void {
    this.selectedBets = this.toggleSelection(this.selectedBets, bet);
  }

  public selectLength(length: string): void {
    this.selectedLengths = this.toggleSelection(this.selectedLengths, length);
  }

  public selectFormat(format: string): void {
    this.selectedFormats = this.toggleSelection(this.selectedFormats, format);
    const candidate: string = this.selectedFormats.includes(format) ? format : this.selectedFormats[0] || '';
    this.selectedFormat = this.helper.ensureSelectedFormat(candidate, this.selectedFormats, this.availableFormat);
  }

  public save(): void {
    this.selectedFormat = this.helper.ensureSelectedFormat(
      this.selectedFormat,
      this.selectedFormats,
      this.availableFormat
    );

    if (!this.funnel || (!this.selectedBets.length && !this.otherBet)) {
      this.submitted = true;
      return;
    }

    if (this.tempAssets.some((asset: SubAsset) => asset.detail === '' || !asset?.bets?.length)) {
      this.submitted = true;
      return;
    }

    this.selectedFormats = [this.selectedFormat];

    const authUser: User = this.authService.getAuthUser();

    const result: Record<string, unknown> = {
      ...(this.asset?.id && { id: this.asset.id }),
      releaseDate: this.helper.convertToTimestamp(this.releaseDate),
      funnel: this.funnel,
      focus: this.focus ?? '',
      type: this.type,
      region: this.region,
      agency: this.agency,
      product: this.product,
      detail: this.detail,
      bets: !this.otherBet ? this.selectedBets : [...this.selectedBets, this.otherBet],
      format: this.selectedFormats,
      length: [this.lengthStatus + ' ' + this.lengthValue],
      categories: this.selectedCategories,
      companyId: authUser.companyId,
      subAsset: this.tempAssets,
      assetType: MASTER_ASSET_TYPE,
    };

    if (this.asset?.id && this.tempAffectedAssets?.[this.asset.id]?.status) {
      result.affectedAsset = this.tempAffectedAssets[this.asset.id];
    }

    this.activeModal.close(result);
  }

  public cancel(): void {
    this.activeModal.close();
  }

  private applyLengthState(state: LengthState): void {
    this.lengthStatus = state.lengthStatus;
    this.lengthValue = state.lengthValue;
    this.lengthValueInSeconds = state.lengthValueInSeconds;
  }

  private toggleSelection<T>(collection: T[], value: T, exclusive: boolean = false): T[] {
    const entries: T[] = collection ?? [];
    const exists: boolean = entries.includes(value);

    if (exclusive) {
      return exists ? [] : [value];
    }

    return exists ? entries.filter((item: T) => item !== value) : [...entries, value];
  }

  private loadFeatureConfigs(): void {
    this.assetPropertiesAvailabilityService
      .getAvailablePropertiesFromApi()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((properties: AssetPropertiesAvailability) => {
        this.applyFeatureConfigs(properties);
      });
  }

  private applyFeatureConfigs(properties: AssetPropertiesAvailability): void {
    this.funnels = properties.funnel;
    this.availableFormat = properties.format;
    this.lengthOptions = properties.length;
    this.betList = properties.platform;
    this.betsOptions = properties.platform;
    this.formatOptions = properties.format;
    this.regions = properties.region;
    this.agencys = properties.agency;
    this.focuses = properties.focus;
    this.types = properties.type;
    this.availableCategories = properties.category;

    this.selectedFormat = this.helper.ensureSelectedFormat(
      this.selectedFormat,
      this.selectedFormats,
      this.availableFormat
    );
    this.initializeFunnelSelection();
  }

  private initializeDefaults(): void {
    this.focus = DEFAULT_ASSET_FOCUS;
    this.type = DEFAULT_ASSET_TYPE;
    this.tempAssets = [];
    this.tempAffectedAssets = {};
    this.applyLengthState(this.helper.initializeLengthState(this.selectedLengths));
    this.selectedFormat = this.helper.ensureSelectedFormat(
      this.selectedFormat,
      this.selectedFormats,
      this.availableFormat
    );
  }

  private initializeFromAsset(): void {
    if (this.asset?.subAsset) {
      this.assets = this.helper.deepClone(this.asset.subAsset);
    }

    if (!this.asset?.id) {
      return;
    }

    this.isNew = false;
    this.mapValues();
  }

  private initializeFunnelSelection(): void {
    this.disableFunnelSelection = Boolean(this.funnel);

    if (!this.funnel && this.funnels?.length) {
      const [firstFunnel] = this.funnels;
      this.funnel = firstFunnel;
    }
  }

  private ensureTempAssets(): void {
    if (!this.tempAssets) {
      this.tempAssets = [];
    }
  }

  private ensureAffectedAssetEntry(assetId: string | number): void {
    if (!this.tempAffectedAssets[assetId]) {
      this.tempAffectedAssets[assetId] = { subAsset: [] };
    }
  }

  private canMutateAffectedAssets(): boolean {
    return Boolean(this.status && this.asset?.id && this.tempAffectedAssets);
  }
}
